import { useState } from 'react';
import camelCase from 'lodash/camelCase';
import { WretcherResponse } from 'wretch';
import { useSearchParams } from 'react-router-dom';
import { formatObjectKeys } from 'src/util';
import useDebouncedState from 'src/hooks/useDebouncedState';

export const API_DEBOUNCE_TIME = 300;
export const DEFAULT_PAGE_SIZE = 10;

export type Sort<TS> = {
  field: TS;
  sort: 'desc' | 'asc';
};

export type Filter<TF> = {
  field: TF;
  value: string;
};

export type Pagination = {
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
};

export interface SearchQuery<TF = string, TS = string> {
  filter: Filter<TF>;
  page: number;
  pageSize: number;
  sort: Sort<TS>;
}

export function useCountryCode() {
  const [searchParams] = useSearchParams();
  return searchParams.get('countryCode')?.toUpperCase();
}

export function useQueryParams<TF = string, TS = string>({
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  sort = { field: '' as TS, sort: 'asc' },
  filter,
}: Pick<SearchQuery<TF, TS>, 'filter'> & Partial<SearchQuery<TF, TS>>) {
  const countryCode = useCountryCode();
  const [searchQuery, setSearchQuery] = useState<SearchQuery<TF, TS>>({
    filter,
    page,
    pageSize,
    sort,
  });

  function extendSearchQuery(props: any) {
    setSearchQuery({
      ...searchQuery,
      ...props,
    });
  }

  return {
    filter: searchQuery.filter,
    pagination: {
      page: searchQuery.page,
      pageSize: searchQuery.pageSize,
      setPage: (page: number) => extendSearchQuery({ page }),
      setPageSize: (pageSize: number) =>
        extendSearchQuery({ page: 1, pageSize }),
    } as Pagination,
    searchQuery: useDebouncedState<any>(
      {
        ...(searchQuery.filter?.value && {
          [searchQuery.filter.field as string]: searchQuery.filter.value,
        }),
        ...(searchQuery.sort?.field && {
          sort: `${searchQuery.sort?.sort === 'desc' ? '-' : ''}${
            searchQuery.sort?.field
          }`,
        }),
        ...(searchQuery.page && { page: searchQuery.page }),
        ...(searchQuery.pageSize && { pageSize: searchQuery.pageSize }),
        countryCode,
      },
      API_DEBOUNCE_TIME
    ),
    setFilter: (filter: any) => extendSearchQuery({ filter, page: 1 }),
    sort: {
      setSort: (sort: Sort<TS>) => extendSearchQuery({ page: 1, sort }),
      sort: searchQuery.sort,
    },
  };
}

const PAGINATION_HEADER = 'X-Pagination';

export type QueryResult<T> = {
  list: T[];
  pagination: PaginationResult;
};

export type PaginationResult = {
  currentPage: number;
  hasNext: boolean;
  hasPrevious: boolean;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

export function parseResponse<T>(res: WretcherResponse) {
  return res.json().then((list) => ({
    list: list.map((item: any) => formatObjectKeys<T>(item, camelCase)),
    pagination: formatObjectKeys<PaginationResult>(
      JSON.parse(res.headers.get(PAGINATION_HEADER) ?? '{}'),
      camelCase
    ),
  }));
}
